import { useState, useEffect } from 'react';
import {
  Plus,
  Edit2,
  Trash2,
  Save,
  X,
  BookOpen,
  Video,
  FileText,
  Download,
  Lock,
  Unlock,
  Eye,
  Search,
  Image as ImageIcon,
  File,
  Library
} from 'lucide-react';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { BeltIndicator } from '../shared/BeltBadge';
import { useBelts } from '../../contexts/BeltContext';
import { adminFetch, getAuthHeaders } from '../../services/adminApi';
import { withMediaCacheKey } from '../../utils/mediaUrl';

import { PageTitle } from './shared';
import { FileUploader } from './shared/FileUploader';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Resource types with icons
const RESOURCE_TYPES = [
  { value: 'video', label: 'Video', icon: Video },
  { value: 'document', label: 'Document', icon: FileText },
  { value: 'image', label: 'Image', icon: ImageIcon },
];

// Get icon for resource type
const getResourceIcon = (type) => {
  const found = RESOURCE_TYPES.find(t => t.value === type);
  return found?.icon || File;
};

// ============================================
// Resource Form Modal
// ============================================
const ResourceForm = ({ resource, programs, onSave, onCancel }) => {
  const { getBeltOptions } = useBelts();
  const beltOptions = getBeltOptions();

  const [formData, setFormData] = useState({
    title: resource?.title || '',
    description: resource?.description || '',
    resource_type: resource?.resource_type || 'video',
    url: resource?.url || '',
    thumbnail_url: resource?.thumbnail_url || '',
    filename: resource?.filename || '',
    content_type: resource?.content_type || '',
    required_belt: resource?.required_belt || '',
    required_program_ids: resource?.required_program_ids || [],
    is_active: resource?.is_active ?? true,
    sort_order: resource?.sort_order || 0,
    tags: resource?.tags || [],
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');

  // Handle file upload
  const handleFileUpload = (uploadResult) => {
    setFormData({
      ...formData,
      url: uploadResult.url,
      thumbnail_url: uploadResult.thumbnail_url || '',
      resource_type: uploadResult.resource_type || formData.resource_type,
      filename: uploadResult.filename,
      content_type: uploadResult.content_type
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...formData.tags, tagInput.trim()]
      });
      setTagInput('');
    }
  };

  const handleRemoveTag = (tag) => {
    setFormData({
      ...formData,
      tags: formData.tags.filter(t => t !== tag)
    });
  };

  const handleProgramToggle = (programId) => {
    const current = formData.required_program_ids;
    if (current.includes(programId)) {
      setFormData({
        ...formData,
        required_program_ids: current.filter(id => id !== programId)
      });
    } else {
      setFormData({
        ...formData,
        required_program_ids: [...current, programId]
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.url) {
      alert('Please upload a file');
      return;
    }

    setSaving(true);

    try {
      const url = resource?.id
        ? `${API_BASE}/api/admin/resources/${resource.id}`
        : `${API_BASE}/api/admin/resources`;

      // Clean up empty values
      const payload = {
        ...formData,
        required_belt: formData.required_belt || null,
        sort_order: parseInt(formData.sort_order) || 0,
      };

      const response = await fetch(url, {
        method: resource?.id ? 'PUT' : 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const savedResource = await response.json();
        onSave(savedResource);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save resource');
      }
    } catch (error) {
      console.error('Failed to save resource:', error);
      alert('Failed to save resource');
    } finally {
      setSaving(false);
    }
  };

  const ResourceIcon = getResourceIcon(formData.resource_type);

  return (
    <Card className="border-2 border-blue-200">
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <BookOpen className="h-5 w-5 text-blue-600" />
          {resource?.id ? 'Edit Resource' : 'Add New Resource'}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Title */}
            <div className="md:col-span-2">
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="e.g., Basic Kata Tutorial"
                className="mt-1"
                required
              />
            </div>

            {/* Description */}
            <div className="md:col-span-2">
              <Label htmlFor="description">Description</Label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Brief description of this resource..."
                className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
                rows={2}
              />
            </div>

            {/* File Upload */}
            <div className="md:col-span-2">
              <Label>Resource File *</Label>
              <div className="mt-1">
                <FileUploader
                  currentFile={formData.url}
                  currentFileName={formData.filename}
                  currentContentType={formData.content_type}
                  onUpload={handleFileUpload}
                  label={formData.url ? 'Replace File' : 'Upload File'}
                />
              </div>
              {formData.url && (
                <div className="mt-2 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <ResourceIcon className="h-4 w-4" />
                  <span>Type: {formData.resource_type}</span>
                </div>
              )}
            </div>

            {/* Access Control Section */}
            <div className="md:col-span-2 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <h4 className="font-medium text-gray-800 flex items-center gap-2 mb-3">
                <Lock className="h-4 w-4" />
                Access Control
              </h4>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                Leave both empty to make this resource available to all students.
              </p>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Required Belt */}
                <div>
                  <Label htmlFor="required_belt">Minimum Belt Required</Label>
                  <select
                    id="required_belt"
                    value={formData.required_belt}
                    onChange={(e) => setFormData({ ...formData, required_belt: e.target.value })}
                    className="w-full mt-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
                  >
                    <option value="">No belt requirement</option>
                    {beltOptions.map(opt => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                </div>

                {/* Sort Order */}
                <div>
                  <Label htmlFor="sort_order">Sort Order</Label>
                  <Input
                    id="sort_order"
                    type="number"
                    value={formData.sort_order}
                    onChange={(e) => setFormData({ ...formData, sort_order: e.target.value })}
                    placeholder="0"
                    className="mt-1"
                  />
                </div>

                {/* Required Programs */}
                <div className="md:col-span-2">
                  <Label>Program Requirements</Label>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    Select programs that have access. If none selected, all programs have access.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {programs.map(program => (
                      <button
                        key={program.id}
                        type="button"
                        onClick={() => handleProgramToggle(program.id)}
                        className={`px-3 py-1 rounded-full text-sm transition-colors ${
                          formData.required_program_ids.includes(program.id)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {program.title || program.name}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="md:col-span-2">
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  placeholder="Add a tag..."
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                />
                <Button type="button" variant="outline" onClick={handleAddTag}>
                  Add
                </Button>
              </div>
              {formData.tags.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {formData.tags.map(tag => (
                    <span
                      key={tag}
                      className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded"
                    >
                      {tag}
                      <button
                        type="button"
                        onClick={() => handleRemoveTag(tag)}
                        className="ml-1 hover:text-blue-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            {/* Active Toggle */}
            <div className="md:col-span-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">Resource is active and visible to students</span>
              </label>
            </div>
          </div>

          <div className="flex space-x-2 mt-4">
            <Button type="submit" disabled={saving} variant="adminPrimary">
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Saving...' : (resource?.id ? 'Update' : 'Add Resource')}
            </Button>
            <Button type="button" variant="adminOutline" onClick={onCancel}>
              <X className="h-4 w-4 mr-2" />
              Cancel
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

// ============================================
// Resource Preview Modal
// ============================================
const ResourcePreviewModal = ({ resource, onClose }) => {
  if (!resource) return null;

  const isVideo = resource.resource_type === 'video' ||
    resource.url?.match(/\.(mp4|webm|mov|avi)$/i) ||
    resource.content_type?.startsWith('video/');

  const isImage = resource.resource_type === 'image' ||
    resource.url?.match(/\.(jpg|jpeg|png|gif|webp|svg)$/i) ||
    resource.content_type?.startsWith('image/');

  const isPDF = resource.url?.match(/\.pdf$/i) ||
    resource.content_type === 'application/pdf';

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-background rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold text-lg">{resource.title}</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-4 flex items-center justify-center bg-gray-100 dark:bg-gray-800">
          {isVideo ? (
            <video
              src={withMediaCacheKey(resource.url)}
              controls
              autoPlay
              className="max-w-full max-h-[70vh] rounded"
            >
              Your browser does not support video playback.
            </video>
          ) : isImage ? (
            <img
              src={resource.url}
              alt={resource.title}
              className="max-w-full max-h-[70vh] object-contain rounded"
            />
          ) : isPDF ? (
            <iframe
              src={resource.url}
              title={resource.title}
              className="w-full h-[70vh] rounded border"
            />
          ) : (
            <div className="text-center py-8">
              <FileText className="h-16 w-16 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 dark:text-gray-400 mb-4">
                Preview not available for this file type.
              </p>
              <Button
                onClick={() => window.open(withMediaCacheKey(resource.url), '_blank')}
                variant="adminPrimary"
              >
                <Download className="h-4 w-4 mr-2" />
                Download File
              </Button>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 dark:bg-gray-800 flex justify-between items-center">
          <div className="text-sm text-gray-500 dark:text-gray-400">
            {resource.filename || resource.resource_type}
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(withMediaCacheKey(resource.url), '_blank')}
            >
              <Download className="h-4 w-4 mr-2" />
              Open in New Tab
            </Button>
            <Button variant="outline" size="sm" onClick={onClose}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Resource List Item
// ============================================
const ResourceListItem = ({ resource, programs, onEdit, onDelete, onToggleActive, onPreview }) => {
  const TypeIcon = getResourceIcon(resource.resource_type);

  const getProgramNames = () => {
    if (!resource.required_program_ids?.length) return 'All Programs';
    return resource.required_program_ids
      .map(id => {
        const program = programs.find(p => p.id === id);
        return program?.title || program?.name || id;
      })
      .join(', ');
  };

  // Check if resource has a displayable thumbnail
  const hasThumbnail = resource.thumbnail_url ||
    (resource.resource_type === 'image' && resource.url);
  const thumbnailUrl = resource.thumbnail_url || resource.url;

  return (
    <div className={`flex items-center gap-4 p-3 bg-background rounded border hover:shadow-sm transition-shadow ${
      !resource.is_active ? 'opacity-60' : ''
    }`}>
      {/* Thumbnail or Icon */}
      {hasThumbnail ? (
        <div
          className="w-16 h-16 rounded overflow-hidden flex-shrink-0 bg-gray-100 dark:bg-gray-700 cursor-pointer"
          onClick={() => onPreview(resource)}
        >
          <img
            src={thumbnailUrl}
            alt={resource.title}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Fallback to icon if image fails to load
              e.target.style.display = 'none';
              e.target.parentElement.innerHTML = `<div class="w-full h-full flex items-center justify-center bg-blue-100"><svg class="h-6 w-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"></path></svg></div>`;
            }}
          />
        </div>
      ) : (
        <div className={`p-2 rounded-full ${resource.is_active ? 'bg-blue-100 dark:bg-blue-900/50' : 'bg-gray-100 dark:bg-gray-700'}`}>
          <TypeIcon className={`h-5 w-5 ${resource.is_active ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400'}`} />
        </div>
      )}
      <div className="flex-1">
        <div className="flex items-center gap-2">
          <span className="font-medium">{resource.title}</span>
          {!resource.is_active && (
            <span className="px-2 py-0.5 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">Inactive</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-sm text-gray-500 dark:text-gray-400 mt-1">
          <span className="capitalize">{resource.resource_type}</span>
          {resource.required_belt && (
            <span className="flex items-center gap-1">
              <Lock className="h-3 w-3" />
              <BeltIndicator belt={resource.required_belt} stripes={0} />
            </span>
          )}
          <span className="text-xs">
            {getProgramNames()}
          </span>
        </div>
        {resource.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {resource.tags.map(tag => (
              <span key={tag} className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded">
                {tag}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onPreview(resource)}
          title="Preview"
        >
          <Eye className="h-4 w-4" />
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onEdit(resource)}>
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onToggleActive(resource)}
          title={resource.is_active ? 'Deactivate' : 'Activate'}
        >
          {resource.is_active ? (
            <Unlock className="h-4 w-4 text-green-600" />
          ) : (
            <Lock className="h-4 w-4 text-gray-400" />
          )}
        </Button>
        <Button variant="ghost" size="sm" onClick={() => onDelete(resource)}>
          <Trash2 className="h-4 w-4 text-red-500" />
        </Button>
      </div>
    </div>
  );
};

// ============================================
// Main Resource Manager Component
// ============================================
const ResourceManager = () => {
  const [resources, setResources] = useState([]);
  const [programs, setPrograms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingResource, setEditingResource] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [previewResource, setPreviewResource] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resourcesRes, programsRes] = await Promise.all([
        fetch(`${API_BASE}/api/admin/resources`, { headers: getAuthHeaders() }),
        fetch(`${API_BASE}/api/programs`)
      ]);

      if (resourcesRes.ok) {
        const data = await resourcesRes.json();
        setResources(data);
      }

      if (programsRes.ok) {
        const data = await programsRes.json();
        setPrograms(data);
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (savedResource) => {
    await fetchData();
    setShowForm(false);
    setEditingResource(null);
  };

  const handleDelete = async (resource) => {
    if (!window.confirm('Are you sure you want to delete this resource?')) return;

    try {
      const response = await adminFetch(`${API_BASE}/api/admin/resources/${resource.id}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      if (response.ok) {
        await fetchData();
      }
    } catch (error) {
      console.error('Failed to delete resource:', error);
    }
  };

  const handleToggleActive = async (resource) => {
    try {
      const response = await adminFetch(`${API_BASE}/api/admin/resources/${resource.id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ ...resource, is_active: !resource.is_active })
      });
      if (response.ok) {
        await fetchData();
      }
    } catch (error) {
      console.error('Failed to toggle resource:', error);
    }
  };

  const startEdit = (resource) => {
    setEditingResource(resource);
    setShowForm(true);
  };

  const startAdd = () => {
    setEditingResource(null);
    setShowForm(true);
  };

  // Filter resources
  const filteredResources = resources.filter(resource => {
    const matchesSearch = !searchTerm ||
      resource.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      resource.tags?.some(t => t.toLowerCase().includes(searchTerm.toLowerCase()));

    const matchesType = !typeFilter || resource.resource_type === typeFilter;

    return matchesSearch && matchesType;
  });

  // Stats
  const activeCount = resources.filter(r => r.is_active).length;
  const inactiveCount = resources.filter(r => !r.is_active).length;
  const videoCount = resources.filter(r => r.resource_type === 'video').length;
  const documentCount = resources.filter(r => r.resource_type === 'document').length;
  const imageCount = resources.filter(r => r.resource_type === 'image').length;

  if (loading) {
    return <div className="text-center py-4">Loading resources...</div>;
  }

  return (
    <div className="space-y-4">
      <PageTitle
        title="Training Resources"
        description="Manage videos, documents, and training materials for students"
        icon={<Library className="h-6 w-6" />}
        actions={
          <Button onClick={startAdd} variant="adminPrimary">
            <Plus className="h-4 w-4 mr-2" />
            Add Resource
          </Button>
        }
      />

      {/* Stats Display with Clickable Filters */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium text-gray-600 dark:text-gray-400">Resource Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {/* Total Resources */}
            <button
              onClick={() => { setTypeFilter(''); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                !typeFilter && !searchTerm
                  ? 'bg-blue-100 dark:bg-blue-900/50 ring-2 ring-blue-500'
                  : 'bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Library className={`h-5 w-5 ${!typeFilter && !searchTerm ? 'text-blue-600 dark:text-blue-400' : 'text-gray-500 dark:text-gray-400'}`} />
                <p className={`text-2xl font-bold ${!typeFilter && !searchTerm ? 'text-blue-900 dark:text-blue-100' : 'text-gray-700 dark:text-gray-300'}`}>
                  {resources.length}
                </p>
              </div>
              <p className={`text-xs font-medium ${!typeFilter && !searchTerm ? 'text-blue-700 dark:text-blue-300' : 'text-gray-600 dark:text-gray-400'}`}>
                Total
              </p>
            </button>

            {/* Active Resources */}
            <button
              onClick={() => { setTypeFilter(''); setSearchTerm(''); }}
              className="p-3 rounded-lg bg-green-50 dark:bg-green-900/30 hover:bg-green-100 dark:hover:bg-green-900/50 transition-all hover:shadow-md"
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Unlock className="h-5 w-5 text-green-600 dark:text-green-400" />
                <p className="text-2xl font-bold text-green-700 dark:text-green-300">{activeCount}</p>
              </div>
              <p className="text-xs font-medium text-green-700 dark:text-green-300">Active</p>
            </button>

            {/* Inactive Resources */}
            <button
              onClick={() => { setTypeFilter(''); setSearchTerm(''); }}
              className="p-3 rounded-lg bg-gray-50 dark:bg-gray-800 hover:bg-gray-100 dark:hover:bg-gray-700 transition-all hover:shadow-md"
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Lock className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                <p className="text-2xl font-bold text-gray-700 dark:text-gray-300">{inactiveCount}</p>
              </div>
              <p className="text-xs font-medium text-gray-600 dark:text-gray-400">Inactive</p>
            </button>

            {/* Videos */}
            <button
              onClick={() => { setTypeFilter('video'); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                typeFilter === 'video'
                  ? 'bg-purple-100 dark:bg-purple-900/50 ring-2 ring-purple-500'
                  : 'bg-purple-50 dark:bg-purple-900/30 hover:bg-purple-100 dark:hover:bg-purple-900/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <Video className={`h-5 w-5 ${typeFilter === 'video' ? 'text-purple-700 dark:text-purple-300' : 'text-purple-600 dark:text-purple-400'}`} />
                <p className={`text-2xl font-bold ${typeFilter === 'video' ? 'text-purple-900 dark:text-purple-100' : 'text-purple-700 dark:text-purple-300'}`}>
                  {videoCount}
                </p>
              </div>
              <p className={`text-xs font-medium ${typeFilter === 'video' ? 'text-purple-800 dark:text-purple-200' : 'text-purple-700 dark:text-purple-300'}`}>
                Videos
              </p>
            </button>

            {/* Documents */}
            <button
              onClick={() => { setTypeFilter('document'); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                typeFilter === 'document'
                  ? 'bg-amber-100 dark:bg-amber-900/50 ring-2 ring-amber-500'
                  : 'bg-amber-50 dark:bg-amber-900/30 hover:bg-amber-100 dark:hover:bg-amber-900/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <FileText className={`h-5 w-5 ${typeFilter === 'document' ? 'text-amber-700 dark:text-amber-300' : 'text-amber-600 dark:text-amber-400'}`} />
                <p className={`text-2xl font-bold ${typeFilter === 'document' ? 'text-amber-900 dark:text-amber-100' : 'text-amber-700 dark:text-amber-300'}`}>
                  {documentCount}
                </p>
              </div>
              <p className={`text-xs font-medium ${typeFilter === 'document' ? 'text-amber-800 dark:text-amber-200' : 'text-amber-700 dark:text-amber-300'}`}>
                Documents
              </p>
            </button>

            {/* Images */}
            <button
              onClick={() => { setTypeFilter('image'); setSearchTerm(''); }}
              className={`p-3 rounded-lg transition-all hover:shadow-md ${
                typeFilter === 'image'
                  ? 'bg-teal-100 dark:bg-teal-900/50 ring-2 ring-teal-500'
                  : 'bg-teal-50 dark:bg-teal-900/30 hover:bg-teal-100 dark:hover:bg-teal-900/50'
              }`}
            >
              <div className="flex items-center justify-center gap-2 mb-1">
                <ImageIcon className={`h-5 w-5 ${typeFilter === 'image' ? 'text-teal-700 dark:text-teal-300' : 'text-teal-600 dark:text-teal-400'}`} />
                <p className={`text-2xl font-bold ${typeFilter === 'image' ? 'text-teal-900 dark:text-teal-100' : 'text-teal-700 dark:text-teal-300'}`}>
                  {imageCount}
                </p>
              </div>
              <p className={`text-xs font-medium ${typeFilter === 'image' ? 'text-teal-800 dark:text-teal-200' : 'text-teal-700 dark:text-teal-300'}`}>
                Images
              </p>
            </button>
          </div>
        </CardContent>
      </Card>

      {/* Search and Filter */}
      {!showForm && (
        <div className="flex flex-wrap gap-2">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search resources..."
                className="pl-9"
              />
            </div>
          </div>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 bg-background text-foreground"
          >
            <option value="">All Types</option>
            {RESOURCE_TYPES.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      )}

      {/* Resource Form */}
      {showForm && (
        <ResourceForm
          resource={editingResource}
          programs={programs}
          onSave={handleSave}
          onCancel={() => { setShowForm(false); setEditingResource(null); }}
        />
      )}

      {/* Resource List */}
      {!showForm && (
        <div className="space-y-2">
          <h4 className="font-medium text-gray-700 dark:text-gray-300 flex items-center gap-2">
            <BookOpen className="h-4 w-4" />
            Resources ({filteredResources.length})
          </h4>
          {filteredResources.length === 0 ? (
            <div className="text-center py-6 text-gray-500 bg-gray-50 dark:bg-gray-800 rounded">
              {searchTerm || typeFilter ? 'No resources match your filters.' : 'No resources added yet.'}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredResources.map(resource => (
                <ResourceListItem
                  key={resource.id}
                  resource={resource}
                  programs={programs}
                  onEdit={startEdit}
                  onDelete={handleDelete}
                  onToggleActive={handleToggleActive}
                  onPreview={setPreviewResource}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Preview Modal */}
      {previewResource && (
        <ResourcePreviewModal
          resource={previewResource}
          onClose={() => setPreviewResource(null)}
        />
      )}
    </div>
  );
};

export default ResourceManager;
